import React, { useState } from 'react';
import { base44 } from "@/api/base44Client";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, RotateCcw, MoveRight, Building2, Mail, Phone, Loader2, X, Globe, CheckSquare, Square } from "lucide-react";
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { format } from "date-fns";
import AuthedImage from '@/components/AuthedImage';
import PageContainer from '@/components/PageContainer';

function ConfirmDialog({ message, note, onConfirm, onCancel, loading, confirmLabel = "Confirm", confirmClass = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      className="mt-2 p-3 bg-card border border-border rounded-xl shadow-sm"
    >
      <p className="text-sm font-medium text-foreground mb-1">{message}</p>
      {note && <p className="text-xs text-muted-foreground mb-3">{note}</p>}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} className="flex-1 min-h-8 text-xs">Cancel</Button>
        <Button size="sm" onClick={onConfirm} disabled={loading} className={`flex-1 min-h-8 text-xs ${confirmClass}`}>
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : confirmLabel}
        </Button>
      </div>
    </motion.div>
  );
}

function DeletedContactCard({ meeting, shows, index }) {
  const queryClient = useQueryClient();
  const [action, setAction] = useState(null); // 'restore' | 'move' | 'purge'
  const [moveShowId, setMoveShowId] = useState('');
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null); // 'badge' | 'person' | null

  const doAction = async () => {
    setLoading(true);
    try {
      if (action === 'restore') {
        await base44.entities.MeetingRequest.update(meeting.id, { status: 'saved' });
        toast.success('Contact restored');
      } else if (action === 'move') {
        const show = shows.find(s => s.id === moveShowId);
        await base44.entities.MeetingRequest.update(meeting.id, {
          status: 'saved',
          trade_show_id: moveShowId,
          trade_show: show?.name || '',
        });
        toast.success(`Moved to ${show?.name || 'show'}`);
      } else if (action === 'purge') {
        await base44.entities.MeetingRequest.delete(meeting.id);
        toast.success('Contact permanently deleted');
      }
      queryClient.invalidateQueries({ queryKey: ['deleted-contacts'] });
    } catch (err) {
      toast.error('Action failed');
    } finally {
      setLoading(false);
      setAction(null);
    }
  };

  const currentShow = shows.find(s => s.id === meeting.trade_show_id);

  return (
    <motion.div key={meeting.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.04 }}>
      <Card className="p-4 bg-card">

        {/* Photos row */}
        {(meeting.badge_image_url || meeting.person_photo_url) && (
          <div className="flex gap-2 mb-3">
            {meeting.badge_image_url && (
              <div className="flex-1 rounded-lg overflow-hidden border border-border cursor-zoom-in" onClick={() => setLightbox('badge')}>
                <AuthedImage src={meeting.badge_image_url} alt="Badge" className="w-full h-28 object-cover" />
                <p className="text-[10px] text-center text-muted-foreground py-0.5 bg-secondary/40">Badge / Card</p>
              </div>
            )}
            {meeting.person_photo_url && (
              <div className="flex-1 rounded-lg overflow-hidden border border-border cursor-zoom-in" onClick={() => setLightbox('person')}>
                <AuthedImage src={meeting.person_photo_url} alt="Person" className="w-full h-28 object-cover" />
                <p className="text-[10px] text-center text-muted-foreground py-0.5 bg-secondary/40">Person</p>
              </div>
            )}
          </div>
        )}

        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-foreground">{meeting.first_name} {meeting.last_name}</p>
            {meeting.company && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                <Building2 className="w-3 h-3" />{meeting.title ? `${meeting.title} · ` : ''}{meeting.company}
              </div>
            )}
            {meeting.email && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                <Mail className="w-3 h-3" />
                <a href={`mailto:${meeting.email}`} className="hover:underline text-accent">{meeting.email}</a>
              </div>
            )}
            {meeting.phone && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                <Phone className="w-3 h-3" />
                <a href={`tel:${meeting.phone}`} className="hover:underline text-accent">{meeting.phone}</a>
              </div>
            )}
            {meeting.website && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                <Globe className="w-3 h-3" />
                <a href={meeting.website.startsWith('http') ? meeting.website : `https://${meeting.website}`} target="_blank" rel="noopener noreferrer" className="hover:underline text-accent truncate">{meeting.website}</a>
              </div>
            )}
            {currentShow && (
              <p className="text-[10px] text-muted-foreground/60 mt-1">Show: {currentShow.name}</p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        {!action && (
          <div className="mt-3 pt-3 border-t border-border flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setAction('restore')} className="flex-1 min-h-8 text-xs">
              <RotateCcw className="w-3.5 h-3.5 mr-1" />Restore
            </Button>
            <Button variant="outline" size="sm" onClick={() => setAction('move')} className="flex-1 min-h-8 text-xs">
              <MoveRight className="w-3.5 h-3.5 mr-1" />Move
            </Button>
            <Button variant="outline" size="sm" onClick={() => setAction('purge')} className="min-h-8 px-3 text-xs text-destructive hover:text-destructive border-destructive/30 hover:bg-destructive/10">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}

        <AnimatePresence>
          {action === 'restore' && (
            <ConfirmDialog
              message="Restore this contact?"
              note="They'll be moved back to their original show list."
              onConfirm={doAction}
              onCancel={() => setAction(null)}
              loading={loading}
              confirmLabel="Yes, Restore"
              confirmClass="bg-emerald-600 hover:bg-emerald-700 text-white"
            />
          )}
          {action === 'move' && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              className="mt-2 p-3 bg-card border border-border rounded-xl shadow-sm"
            >
              <p className="text-sm font-medium text-foreground mb-2">Move to which show?</p>
              <Select value={moveShowId} onValueChange={setMoveShowId}>
                <SelectTrigger className="h-9 mb-3 bg-secondary/50 border-0">
                  <SelectValue placeholder="Select a show..." />
                </SelectTrigger>
                <SelectContent>
                  {shows.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setAction(null)} className="flex-1 min-h-8 text-xs">Cancel</Button>
                <Button size="sm" onClick={doAction} disabled={!moveShowId || loading} className="flex-1 min-h-8 text-xs bg-accent hover:bg-accent/90 text-accent-foreground">
                  {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Move'}
                </Button>
              </div>
            </motion.div>
          )}
          {action === 'purge' && (
            <ConfirmDialog
              message="Permanently delete this contact?"
              note="This cannot be undone. The contact will be gone forever."
              onConfirm={doAction}
              onCancel={() => setAction(null)}
              loading={loading}
              confirmLabel="Yes, Delete Forever"
              confirmClass="bg-destructive hover:bg-destructive/90 text-white"
            />
          )}
        </AnimatePresence>
      </Card>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
          >
            <button className="absolute top-4 right-4 p-2 bg-white/20 rounded-full hover:bg-white/30" onClick={() => setLightbox(null)}>
              <X className="w-5 h-5 text-white" />
            </button>
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            >
              <AuthedImage
                src={lightbox === 'badge' ? meeting.badge_image_url : meeting.person_photo_url}
                alt={lightbox === 'badge' ? 'Badge full size' : 'Person full size'}
                className="max-w-full max-h-full rounded-xl object-contain"
                fallbackClassName="w-64 h-64 rounded-xl"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function DeletedContacts() {
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);

  const { data: meetings = [], isLoading } = useQuery({
    queryKey: ['deleted-contacts'],
    queryFn: async () => {
      const me = await base44.auth.me();
      return base44.entities.MeetingRequest.filter({ created_by_id: me.id, status: 'deleted' }, '-updated_date', 200);
    },
  });

  const { data: shows = [] } = useQuery({
    queryKey: ['shows'],
    queryFn: () => base44.entities.TradeShow.list('-start_date', 50),
  });

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === meetings.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(meetings.map(m => m.id)));
    }
  };

  const bulkDelete = async () => {
    setBulkDeleting(true);
    try {
      await Promise.all([...selectedIds].map(id => base44.entities.MeetingRequest.delete(id)));
      toast.success(`${selectedIds.size} contact${selectedIds.size !== 1 ? 's' : ''} permanently deleted`);
      setSelectedIds(new Set());
      setShowBulkConfirm(false);
      queryClient.invalidateQueries({ queryKey: ['deleted-contacts'] });
    } catch (err) {
      toast.error('Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const allSelected = meetings.length > 0 && selectedIds.size === meetings.length;

  return (
    <div className="min-h-screen bg-background safe-top pb-24">
      <PageContainer className="px-4 py-6">
        <div className="flex items-center gap-3 mb-4">
          <Link to="/shows">
            <Button variant="outline" className="min-h-9 px-4 text-sm">Back</Button>
          </Link>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-foreground">Deleted Contacts</h1>
            <p className="text-sm text-muted-foreground">{meetings.length} contact{meetings.length !== 1 ? 's' : ''}</p>
          </div>
        </div>

        {/* Bulk actions bar */}
        {meetings.length > 0 && !isLoading && (
          <div className="flex items-center gap-2 mb-4 p-3 bg-card rounded-xl border border-border">
            <button onClick={toggleSelectAll} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
              {allSelected ? <CheckSquare className="w-4 h-4 text-accent" /> : <Square className="w-4 h-4" />}
              <span>{allSelected ? 'Deselect All' : 'Select All'}</span>
            </button>
            {selectedIds.size > 0 && (
              <>
                <span className="text-xs text-muted-foreground ml-1">({selectedIds.size} selected)</span>
                <div className="ml-auto">
                  {!showBulkConfirm ? (
                    <Button size="sm" variant="destructive" onClick={() => setShowBulkConfirm(true)} className="min-h-8 text-xs gap-1">
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete {selectedIds.size}
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setShowBulkConfirm(false)} className="min-h-8 text-xs">Cancel</Button>
                      <Button size="sm" variant="destructive" onClick={bulkDelete} disabled={bulkDeleting} className="min-h-8 text-xs">
                        {bulkDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : `Yes, Delete ${selectedIds.size} Forever`}
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        <div className="space-y-3">
          {isLoading ? (
            Array(3).fill(0).map((_, i) => (
              <div key={i} className="bg-card rounded-xl p-4 animate-pulse h-20" />
            ))
          ) : meetings.length === 0 ? (
            <div className="text-center py-16">
              <Trash2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground font-medium">No deleted contacts</p>
            </div>
          ) : (
            meetings.map((m, i) => (
              <div key={m.id} className="flex gap-2 items-start">
                <button onClick={() => toggleSelect(m.id)} className="mt-4 shrink-0">
                  {selectedIds.has(m.id)
                    ? <CheckSquare className="w-5 h-5 text-accent" />
                    : <Square className="w-5 h-5 text-muted-foreground" />}
                </button>
                <div className="flex-1 min-w-0">
                  <DeletedContactCard meeting={m} shows={shows} index={i} />
                </div>
              </div>
            ))
          )}
        </div>
        <div className="h-8" />
      </PageContainer>
    </div>
  );
}